import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import JSZip from 'jszip';
import { Observable } from 'rxjs';
import { ServicePathsSettings } from 'src/app/core/configuration/service-paths-settings';
import { UserProfileFlagStore } from 'src/app/utility/store/user-profile-flag';
import { DocumentoAllegato } from '../../models/documento-allegato';
import { EXTENSIONS } from './eml/eml-preview.component';
import { UrlSettingsCore } from 'src/app/core/configuration/url-settings-core';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class DocPreviewUtilityService {
  private buffer: ArrayBuffer | string = null;
  constructor(
    private http: HttpClient,
    private router: Router
  ) { }

  setBuffer(buffer: ArrayBuffer | string) {
    this.buffer = buffer;
  }

  getBuffer() {
    return this.buffer;
  }

  base64ToUint8Array(base64: string): Uint8Array {
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }

  arrayBufferToBase64(arrayBuffer: ArrayBuffer): string {
    const uint8Array = new Uint8Array(arrayBuffer); // Converti in Uint8Array
    let binaryString = '';

    // Converte l'ArrayBuffer in una stringa binaria
    for (let i = 0; i < uint8Array.length; i++) {
      binaryString += String.fromCharCode(uint8Array[i]);
    }

    // Converte la stringa binaria in base64
    return btoa(binaryString);
  }


  async onFileZipSelected(arrayBuffer: ArrayBuffer | string): Promise<Array<{
    name: string;
    mimeType: string;
    content: ArrayBuffer | string;
  }>> {
    const zip = await JSZip.loadAsync(arrayBuffer);

    const previews: Array<{
      name: string;
      mimeType: string;
      content: ArrayBuffer | string;
    }> = [];

    const mimeTypesMap: Record<string, string> = {
      png: 'image/png',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      gif: 'image/gif',
      webp: 'image/webp',
      txt: 'text/plain',
      json: 'application/json',
      md: 'text/markdown',
      csv: 'text/csv',
      eml: 'message/rfc822',
      pdf: 'application/pdf',
      doc: 'application/msword',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      xls: 'application/vnd.ms-excel',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    };

    const filePromises = Object.keys(zip.files).map(async (relativePath) => {
      const zipEntry = zip.files[relativePath];

      if (!zipEntry.dir) {
        const ext = relativePath.split('.').pop()?.toLowerCase() || '';
        const mimeType = mimeTypesMap[ext] || 'application/octet-stream';

        const buffer = await zipEntry.async('arraybuffer');

        previews.push({
          name: relativePath,
          mimeType,
          content: buffer
        });
      }
    });

    await Promise.all(filePromises);
    return previews;
  }


  findMaxSizeByContentype(contentType) {
    let maxPreviewSize = UserProfileFlagStore.getInstance().MAX_PREVIEW_FILE_SIZE;
    if(!maxPreviewSize) return null;
    for (const key in maxPreviewSize) {
        if (maxPreviewSize[key].contentType === contentType) {
            return maxPreviewSize[key].maxSize;
        }
    }
    return maxPreviewSize['default'].maxSize;
  }

  /**
   * extractPdfFromP7m
   * Extracts PDF file encapsuleted in a P7M wrapper
   */
  public extractPdfFromP7m(payload:any): Observable<any> {
    // ServicePathsSettings.GESTIONE_PRATICHE_PATH
    let url = ServicePathsSettings.DOMINIO + ServicePathsSettings.OPERATIONS_PATH +  "/p7m/preview";
    return this.http.post(url, payload,{
        responseType: 'arraybuffer',
        observe: 'response',
        withCredentials: true,
      });
  }

  onDecodeUri(dataURI: string) {
    const splitDataURI = dataURI.split(',')
    return decodeURI(splitDataURI[1])
  }

  parseContentDisposition(contentDisposition: string | null): { filename: string | null, extension: string | null } {
  if (!contentDisposition) {
    return { filename: null, extension: null };
  }

  let filename: string | null = null;

  // Caso moderno: filename*
  const filenameStarMatch = contentDisposition.match(/filename\*\=([^']*)''(.+)/);
  if (filenameStarMatch) {
    filename = decodeURIComponent(filenameStarMatch[2]);
  }

  // Caso classico: filename="xxx"
  if (!filename) {
    const filenameMatch = contentDisposition.match(/filename="(.+)"/);
    if (filenameMatch) {
      filename = filenameMatch[1];
    }
  }

  // Caso senza virgolette: filename=xxx
  if (!filename) {
    const filenameSimpleMatch = contentDisposition.match(/filename=([^;]+)/);
    if (filenameSimpleMatch) {
      filename = filenameSimpleMatch[1];
    }
  }

  // Estrazione estensione
  let extension: string | null = null;
  if (filename) {
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex > -1 && lastDotIndex < filename.length - 1) {
      extension = filename.substring(lastDotIndex + 1);
    }
  }

  return {
    filename,
    extension
  };
}

  canViewFile(documento: DocumentoAllegato){
    let maxPreviewSize = this.findMaxSizeByContentype(documento.contentType);
    // controllo che il contenuto del file non superi la dimensione massima consentita
    // console.log(documento.contentSize, Number(maxPreviewSize))
    if(documento.contentSize > Number(!!maxPreviewSize ? maxPreviewSize : documento.contentSize)) {
      return false;
    };
    return documento && 
    documento.nomeFile && (
      documento.nomeFile.includes(".pdf") || 
      documento.nomeFile.includes(".eml") || 
      documento.nomeFile.includes(".zip") || 
      documento.nomeFile.includes(".p7m")
    );
  }

  checkMaxLimitExceeded(documento: DocumentoAllegato):boolean {
    let maxPreviewSize = this.findMaxSizeByContentype(documento.contentType);
    if(documento.contentSize > Number(!!maxPreviewSize ? maxPreviewSize : documento.contentSize)) {
      return true;
    }
    return false;
  }

  previewNavigate(documento: DocumentoAllegato, contentType: string) {
    let fullUrl = null;
    let urlTree = null;
    let urlSerialized = null;
    switch (contentType) {
      case EXTENSIONS.EML:
        urlTree = this.router.createUrlTree([
          UrlSettingsCore.URL_EML_FILE_PREVIEW,
          documento.id,
        ]);
        break;
      case EXTENSIONS.ZIP:
        urlTree = this.router.createUrlTree([
          UrlSettingsCore.URL_ZIP_FILE_PREVIEW,
          documento.id,
        ]);
        break;
      case EXTENSIONS.PDF:
        urlTree = this.router.createUrlTree([
          UrlSettingsCore.URL_PDF_FILE_PREVIEW,
          documento.id,
        ]);
        break;
      case EXTENSIONS.P7M:
        urlTree = this.router.createUrlTree([
          UrlSettingsCore.URL_PDF_FILE_PREVIEW,
          documento.id,
          "p7m",
        ]);
        break;
      default:
        break;
    }
    if (!urlTree) return;
    urlSerialized = this.router.serializeUrl(urlTree);
    fullUrl = `${window.location.origin + (location.pathname && location.pathname !== "" ? location.pathname : "/")}#${urlSerialized}?nomeFile=${documento.nomeFile}`;
    window.open(fullUrl, "_blank");
  }
}
